import React from "react";
import cn from "./helpers/cn";

export type ChipColor = "default" | "primary" | "secondary";
export type ChipVariant = "default" | "outlined";
export type ChipSize = "small" | "medium";

interface ChipProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onClick" | "children"> {
  component?: React.ElementType;
  label?: React.ReactNode;
  icon?: React.ReactElement;
  avatar?: React.ReactElement;
  deleteIcon?: React.ReactElement;
  onDelete?: ((event: React.MouseEvent | React.KeyboardEvent) => void) | null;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  clickable?: boolean;
  disabled?: boolean;
  color?: ChipColor;
  variant?: ChipVariant;
  size?: ChipSize;
}

// Cores por (color, variant). Reproduzem palette do MUI v4.
const colorClasses: Record<ChipColor, { default: string; outlined: string; }> = {
  default: {
    default: "bg-slate-200 text-slate-900",
    outlined: "bg-transparent text-slate-700 border border-slate-300",
  },
  primary: {
    default: "bg-primary text-contrast-text-primary",
    outlined: "bg-transparent text-primary border border-primary",
  },
  secondary: {
    default: "bg-secondary text-contrast-text-secondary",
    outlined: "bg-transparent text-secondary border border-secondary",
  },
};

const DefaultDeleteIcon = (props: React.SVGAttributes<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" />
  </svg>
);

const Chip = React.forwardRef<HTMLDivElement, ChipProps>((props, ref) => {
  const {
    className,
    component: Component = "div",
    label,
    icon,
    avatar,
    deleteIcon,
    onDelete,
    onClick,
    clickable: clickableProp,
    disabled = false,
    color = "default",
    variant = "default",
    size = "medium",
    ...other
  } = props;

  const hasDelete = !!onDelete;
  const clickable = clickableProp ?? (!!onClick);
  const isInteractive = clickable && !disabled;

  const handleDeleteClick = (event: React.MouseEvent) => {
    // Não dispara o onClick do Chip ao clicar no botão de delete.
    event.stopPropagation();
    if (disabled) return;
    onDelete?.(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // Suporte do MUI: Backspace/Delete dispara onDelete; Enter dispara onClick.
    if (event.target !== event.currentTarget) return;
    if (hasDelete && (event.key === "Backspace" || event.key === "Delete")) {
      event.preventDefault();
      onDelete?.(event);
    } else if (isInteractive && event.key === "Enter") {
      event.preventDefault();
      onClick?.(event as unknown as React.MouseEvent<HTMLDivElement>);
    }
  };

  // Tamanho: medium altura 32px (size-8), small altura 24px (size-6).
  const sizeClasses = size === "small"
    ? "h-6 text-[0.8125rem] px-3"
    : "h-8 text-[0.8125rem] px-3";

  // Avatar/Icon size adjustment para encaixar nas bordas (sem padding lateral à esquerda).
  const hasLeading = !!(avatar || icon);

  return (
    <Component
      ref={ref}
      tabIndex={isInteractive || hasDelete ? 0 : -1}
      role={isInteractive ? "button" : undefined}
      aria-disabled={disabled || undefined}
      onClick={isInteractive ? onClick : undefined}
      onKeyDown={handleKeyDown}
      className={cn(
        "inline-flex items-center justify-center max-w-full rounded-full font-normal",
        "transition-colors outline-none whitespace-nowrap align-middle",
        sizeClasses,
        hasLeading && "pl-0",
        colorClasses[color][variant],

        isInteractive && [
          "cursor-pointer",
          variant === "outlined"
            ? color === "default" ? "hover:bg-slate-500/10 focus-visible:bg-slate-500/10"
              : color === "primary" ? "hover:bg-primary/10 focus-visible:bg-primary/10"
                : "hover:bg-secondary/10 focus-visible:bg-secondary/10"
            : color === "default" ? "hover:bg-slate-300 focus-visible:bg-slate-300"
              : color === "primary" ? "hover:bg-primary/90 focus-visible:bg-primary/90"
                : "hover:bg-secondary/90 focus-visible:bg-secondary/90",
        ],

        disabled && "opacity-50 pointer-events-none",
        className
      )}
      {...other}
    >
      {avatar && React.cloneElement(avatar, {
        className: cn(
          // Avatar fica encostado na borda esquerda, com tamanho proporcional à altura do Chip.
          size === "small" ? "w-[18px] h-[18px] mr-1.5 -ml-0.5" : "w-6 h-6 mr-1.5 -ml-1",
          "text-[0.75rem]",
          (avatar.props as { className?: string; }).className
        ),
      })}

      {!avatar && icon && React.cloneElement(icon, {
        className: cn(
          // Ícone fica colado na borda esquerda (margin-left negativo).
          size === "small" ? "w-[18px] h-[18px] ml-1 -mr-1.5" : "w-5 h-5 ml-1.5 -mr-1.5",
          "text-current",
          (icon.props as { className?: string; }).className
        ),
      })}

      <span className={cn("px-3 overflow-hidden text-ellipsis", (avatar || icon) && "pl-2")}>
        {label}
      </span>

      {hasDelete && (
        deleteIcon
          ? React.cloneElement(deleteIcon, {
            onClick: handleDeleteClick,
            className: cn(
              "shrink-0 cursor-pointer",
              size === "small" ? "w-4 h-4 -mr-1.5 ml-1" : "w-[22px] h-[22px] -mr-1.5 ml-1",
              "opacity-70 hover:opacity-100",
              (deleteIcon.props as { className?: string; }).className
            ),
          })
          : <DefaultDeleteIcon
            onClick={handleDeleteClick}
            aria-label="delete"
            className={cn(
              "shrink-0 cursor-pointer",
              size === "small" ? "w-4 h-4 -mr-1.5 ml-1" : "w-[22px] h-[22px] -mr-1.5 ml-1",
              "opacity-70 hover:opacity-100"
            )}
          />
      )}
    </Component>
  );
});

Chip.displayName = "Chip";

export default Chip;